import { cn } from "@-/com.npmjs.cn";
import { LinearGradient as LinearGradientFromExpoLinearGradient } from "@-/com.npmjs.expo-linear-gradient";
import { View, type ViewProps } from "@-/com.npmjs.react-native";
import { GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useEffect, type ComponentProps, type ReactNode } from "react";
import { useCSSVariable, withUniwind } from "@-/com.npmjs.uniwind";
import {
  FrostedBackdropProvider,
  FrostedBackdropTarget,
  FrostedSurface,
} from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeElementContainer,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import type { useTopSheetDismissMotion } from "#use-top-sheet-dismiss-motion";
import type { useTopSheetMotion } from "#use-top-sheet-motion";
import type { useTopSheetRevealMotion } from "#use-top-sheet-reveal-motion";

const LinearGradient = withUniwind(LinearGradientFromExpoLinearGradient);
/** The free edge's border: the padding (`pb-px`) that stands in for it beneath the handle. */
const SHEET_BORDER_WIDTH = 1;
const HSL_CHANNEL_SEPARATOR = /\s+/u;

/**
 * Every top sheet overlays its scroller with the same free-edge blur and drag handle. A `reveal`
 * sheet can instead grow from the bottom of its container (`edge="bottom"`): the handle and blur
 * then sit along its top edge, and its exposed corners are the top ones.
 *
 * A `header` (top sheets only) stays pinned above the scroller, along the anchor, and the
 * scroller's rows fade out as they pass beneath it. Under a dismiss motion it drags the sheet like
 * the handle does and rides down as the sheet collapses, so it stays in sight while the content
 * slides away beneath it, until it rests on the handle with the content covered beneath the
 * handle's material. The header and handle then slide behind the anchor together, unless the
 * motion's `peekHeight` keeps them in sight. `clip` instead holds the content at its open
 * position while the free edge moves upward over it; the anchored header never translates, and
 * once the handle reaches it, whatever of the header lies beneath the handle is covered too.
 */
export function TopSheet({
  children,
  className,
  edge = "top",
  handle,
  header,
  mode = "resize",
  motion,
  viewportInset = 0,
  ...props
}: Omit<ViewProps, "style"> & {
  /** Only `reveal` sheets can grow from the bottom edge. */
  edge?: "top" | "bottom";
  handle: ReactNode;
  /** Pinned above the scroller; see above. */
  header?: ReactNode;
  mode?: "resize" | "translate" | "reveal" | "clip";
  motion:
    | ReturnType<typeof useTopSheetDismissMotion>
    | ReturnType<typeof useTopSheetMotion>
    | ReturnType<typeof useTopSheetRevealMotion>;
  /** May be animated, such as a `translate` sheet's height following its content. */
  style?: ComponentProps<typeof Animated.View>["style"];
  viewportInset?: number;
}) {
  const { value } = motion;
  const visibleHeight = "visibleHeight" in motion ? motion.visibleHeight : undefined;
  const isBottom = edge === "bottom";
  const handleHeight = useSharedValue(0);
  const viewportInsetValue = useSharedValue(viewportInset);
  useEffect(() => {
    viewportInsetValue.set(viewportInset);
  }, [viewportInset, viewportInsetValue]);
  const surfaceStyle = useAnimatedStyle(() =>
    mode === "clip" && visibleHeight !== undefined
      ? { height: visibleHeight.get() }
      : mode === "translate"
        ? {
            transform: [{ translateY: value.get() }],
          }
        : mode === "reveal"
          ? {
              height: Math.max(0, value.get()),
              // Hide the border/handle at zero without clipping the open surface's shadow.
              overflow: value.get() > 0 ? "visible" : "hidden",
            }
          : {},
  );
  const viewportStyle = useAnimatedStyle(() =>
    mode === "resize"
      ? {
          height: Math.max(0, viewportInsetValue.get() + value.get()) + handleHeight.get(),
        }
      : {},
  );
  const sheetHeight = "sheetHeight" in motion ? motion.sheetHeight : undefined;
  // Keep the open content viewport fixed while the surface's free edge clips it. Resizing the
  // scroller itself would relayout/reposition its content during a handle drag.
  const contentViewportStyle = useAnimatedStyle(() =>
    mode === "clip" && sheetHeight !== undefined ? { height: sheetHeight.get() } : {},
  );
  const headerHeight = useSharedValue(0);
  // The header resting on the handle, the border beneath them included.
  const dockedHeight = useDerivedValue(
    () => headerHeight.get() + handleHeight.get() + SHEET_BORDER_WIDTH,
  );
  // The header rides down by as much of the sheet as has slid behind the anchor, until it rests on
  // the handle. From there it leaves with the sheet: a sheet that collapses out of sight takes it
  // along, and one that peeks keeps it in sight.
  const headerStyle = useAnimatedStyle(() =>
    sheetHeight === undefined || mode === "clip"
      ? {}
      : {
          transform: [
            {
              translateY: Math.min(
                Math.max(0, -value.get()),
                Math.max(0, sheetHeight.get() - dockedHeight.get()),
              ),
            },
          ],
        },
  );
  // The sheet's surface covers the content beneath the handle as the gap between the header and the
  // handle closes, so a collapsing sheet shows none of it through the handle's material. A cover
  // rather than the content's opacity: the handle's scroll edge effect only attaches to a scroller
  // it can see. A `clip` sheet's content holds still while its free edge rises over it, so there the
  // cover rides up with the handle; at the viewport's bottom it would be clipped out of sight.
  const handleCoverStyle = useAnimatedStyle(() =>
    sheetHeight === undefined
      ? { opacity: 0 }
      : {
          ...(mode === "clip" && visibleHeight !== undefined
            ? { top: visibleHeight.get() - handleHeight.get() }
            : { bottom: 0 }),
          height: handleHeight.get(),
          opacity:
            1 -
            Math.min(
              1,
              Math.max(
                0,
                (sheetHeight.get() + value.get() - dockedHeight.get()) /
                  Math.max(1, handleHeight.get()),
              ),
            ),
        },
  );
  return (
    <Animated.View
      {...props}
      className={cn(
        isBottom
          ? // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Top sheets use platform-style 28pt exposed corners.
            "absolute inset-x-0 bottom-0 z-10 rounded-t-[28px]"
          : // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Top sheets use platform-style 28pt exposed corners.
            "absolute inset-x-0 top-0 z-10 rounded-b-[28px]",
        className,
        "bg-surface shadow-xs",
      )}
      style={[props.style, surfaceStyle]}
    >
      {mode === "translate" ? (
        // A sheet pulled down past its open position stretches: the gap above it stays the
        // sheet's surface instead of showing the page behind. Overdrag resistance stops at 48pt.
        <View
          className="absolute inset-x-0 bottom-full h-16 border-x border-muted-foreground/35 bg-surface"
          pointerEvents="none"
          testID="top-sheet-overdrag-fill"
        />
      ) : null}
      <FrostedBackdropProvider>
        <View
          className={cn(
            // Padding stands in for the border, which the overlay below draws above the content.
            isBottom
              ? // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Clip to the sheet's platform-style exposed corners.
                "overflow-hidden rounded-t-[28px] bg-surface px-px pt-px"
              : // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Clip to the sheet's platform-style exposed corners.
                "overflow-hidden rounded-b-[28px] bg-surface px-px pb-px",
            mode !== "resize" && "flex-1",
          )}
        >
          <Animated.View
            className={cn(
              "overflow-hidden bg-surface",
              mode !== "resize" && mode !== "clip" && "flex-1",
            )}
            style={[viewportStyle, contentViewportStyle]}
          >
            <FrostedBackdropTarget className="flex-1">
              {header === undefined ? null : (
                <TopSheetHeader
                  gesture={"header" in motion ? motion.header : undefined}
                  onHeightChange={(height) => headerHeight.set(height)}
                  style={headerStyle}
                >
                  {header}
                </TopSheetHeader>
              )}
              {motion.contentDraggable ? (
                <GestureDetector gesture={motion.content}>{children}</GestureDetector>
              ) : (
                children
              )}
              {header === undefined ? null : (
                <Animated.View
                  className="absolute inset-x-0 bg-surface"
                  pointerEvents="none"
                  style={handleCoverStyle}
                  testID="top-sheet-handle-cover"
                />
              )}
            </FrostedBackdropTarget>
          </Animated.View>
          <View
            className={isBottom ? "absolute inset-x-0 top-0" : "absolute inset-x-0 bottom-0"}
            onLayout={(event) => handleHeight.set(event.nativeEvent.layout.height)}
          >
            <GestureDetector gesture={motion.handle}>
              {isScrollEdgeEffectSupported ? (
                <ScrollEdgeElementContainer
                  coversBounds
                  edge={isBottom ? "top" : "bottom"}
                  // The handle blurs the sheet's own content, wherever the sheet has gone: a
                  // collapsed sheet carries it out of sight behind its anchor, over the anchor's
                  // own scrollers (a strip of tabs), which must stay sharp.
                  scrollerScope="surface"
                  testID="top-sheet-handle-material"
                >
                  {handle}
                </ScrollEdgeElementContainer>
              ) : (
                <FrostedSurface testID="top-sheet-handle-material">{handle}</FrostedSurface>
              )}
            </GestureDetector>
          </View>
          {/* iOS draws a non-uniform border beneath the children, whose square corners then hide
                it along the rounded corners; drawn last, the border stays on top. */}
          <View
            className={
              isBottom
                ? // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Trace the sheet's exposed corners.
                  "absolute inset-0 rounded-t-[28px] border border-b-0 border-muted-foreground/35"
                : // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Trace the sheet's exposed corners.
                  "absolute inset-0 rounded-b-[28px] border border-t-0 border-muted-foreground/35"
            }
            pointerEvents="none"
            testID="top-sheet-border"
          />
        </View>
      </FrostedBackdropProvider>
    </Animated.View>
  );
}

/**
 * Content passing under the header dissolves into the sheet over this eased ramp (opacity only, the
 * same colour at every stop), so the scroller's first rows thin out instead of being cut off.
 */
const HEADER_EDGE_FADE = {
  alphas: [1, 0.6, 0.15, 0],
  locations: [0, 0.3, 0.65, 1],
} as const;

/**
 * Drawn above the content, with the fade along its lower edge. It drags the sheet like the handle
 * does when its motion provides a gesture for it.
 */
function TopSheetHeader({
  children,
  gesture,
  onHeightChange,
  style,
}: {
  children: ReactNode;
  gesture: ReturnType<typeof useTopSheetDismissMotion>["header"] | undefined;
  onHeightChange: (height: number) => void;
  style: ComponentProps<typeof Animated.View>["style"];
}) {
  // The raw token stays HSL channels on both native and web; --color-surface is already wrapped
  // in hsl() on web. Every stop matches bg-surface and varies only in opacity.
  const surfaceChannels = useCSSVariable("--surface");
  const surfaceColor = (alpha: number) =>
    `hsla(${String(surfaceChannels).trim().split(HSL_CHANNEL_SEPARATOR).join(", ")}, ${alpha})`;
  const header = (
    <Animated.View
      className="z-1"
      collapsable={false}
      onLayout={(event) => onHeightChange(event.nativeEvent.layout.height)}
      style={style}
      testID="top-sheet-header"
    >
      {children}
      {surfaceChannels === undefined ? null : (
        <LinearGradient
          className="absolute inset-x-0 top-full h-3"
          colors={[
            surfaceColor(HEADER_EDGE_FADE.alphas[0]),
            surfaceColor(HEADER_EDGE_FADE.alphas[1]),
            surfaceColor(HEADER_EDGE_FADE.alphas[2]),
            surfaceColor(HEADER_EDGE_FADE.alphas[3]),
          ]}
          locations={HEADER_EDGE_FADE.locations}
          pointerEvents="none"
          testID="top-sheet-header-edge-fade"
        />
      )}
    </Animated.View>
  );
  return gesture === undefined ? (
    header
  ) : (
    <GestureDetector gesture={gesture}>{header}</GestureDetector>
  );
}
